import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, RotateCcw, Sparkles, User, Zap, Info, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  'Hi Kolly',
  'Add property Rose PG in Gurgaon',
  'Add 5 rooms on 1st floor',
  'Add 3 double rooms on 2nd floor at 8000 rent',
  'Pehla floor pe 4 kamre add kar',
  'Show my properties',
  'Show rooms in Rose PG',
  'Help'
];

const KollyTestChat = () => {
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [phone, setPhone] = useState('1234567890');
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [session, setSession] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const endRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/whatsapp/status`, { headers });
      setStatus(r.data);
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async (p) => {
    try {
      const r = await axios.get(`${API}/whatsapp/messages`, { headers, params: { phone: p, limit: 100 } });
      setMessages(
        (r.data.messages || []).map((m) => ({
          from: m.direction === 'in' ? 'user' : 'kolly',
          text: m.text,
          at: m.at
        }))
      );
    } catch (_) {
      setMessages([]);
    }
    try {
      const s = await axios.get(`${API}/whatsapp/session`, { headers, params: { phone: p } });
      setSession(s.data);
    } catch (_) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { loadHistory(phone); }, [phone, loadHistory]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text) => {
    const t = (text ?? inputText).trim();
    if (!t || sending) return;
    setSending(true);
    setMessages((m) => [...m, { from: 'user', text: t, at: new Date().toISOString(), pending: true }]);
    setInputText('');
    try {
      const r = await axios.post(`${API}/whatsapp/test-message`, { phone, text: t }, { headers });
      setMessages((m) => [
        ...m.map((x) => (x.pending ? { ...x, pending: false } : x)),
        { from: 'kolly', text: r.data.reply, intent: r.data.intent, slots: r.data.slots, at: new Date().toISOString() }
      ]);
      setSession({ state: r.data.session || {} });
    } catch (e) {
      toast({ title: 'Kolly error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      setMessages((m) => m.filter((x) => !x.pending));
    } finally {
      setSending(false);
    }
  };

  const resetSession = async () => {
    if (!window.confirm("Clear Kolly's memory for this number?")) return;
    try {
      await axios.post(`${API}/whatsapp/session/reset?phone=${phone}`, {}, { headers });
      toast({ title: 'Session reset' });
      loadHistory(phone);
    } catch (e) {
      toast({ title: 'Reset failed', variant: 'destructive' });
    }
  };

  const webhookUrl = `${window.location.origin.replace(/^https?:\/\//, '')}/api/whatsapp/webhook`;
  const verifyToken = 'rentkollect-kolly-2026-verify';

  return (
    <div className="space-y-5" data-testid="admin-kolly-test-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 via-green-500 to-teal-500 flex items-center justify-center">
              <Sparkles size={18} className="text-white" />
            </div>
            Kolly — WhatsApp Test Lab
          </h1>
          <p className="text-gray-600 mt-1">
            Chat with Kolly as if you were on WhatsApp. Zero Meta setup required — lets you validate flows before going live.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              data-testid="kolly-status-pill"
              className={`text-[10px] font-bold px-2 py-1 rounded-full border ${
                status.whatsapp_enabled
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-amber-100 text-amber-700 border-amber-200'
              }`}
            >
              {status.whatsapp_enabled ? '● LIVE ON WHATSAPP' : '⚠ WHATSAPP OFFLINE (test-only)'}
            </span>
          )}
        </div>
      </div>

      {/* Setup banner — only show if WhatsApp not enabled */}
      {status && !status.whatsapp_enabled && (
        <Card className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
          <div className="flex items-start gap-3">
            <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1 text-sm text-amber-900">
              <p className="font-bold mb-1.5">Kolly runs locally right now. To flip the real WhatsApp switch:</p>
              <ol className="list-decimal ml-5 space-y-1 text-amber-800 text-[13px]">
                <li>Finish Meta Business Verification + get <code className="bg-white/60 px-1 rounded">Phone Number ID</code>, <code className="bg-white/60 px-1 rounded">API Token</code>, <code className="bg-white/60 px-1 rounded">App Secret</code>.</li>
                <li>Share them — I'll set them in <code className="bg-white/60 px-1 rounded">.env</code> and flip <code className="bg-white/60 px-1 rounded">WHATSAPP_ENABLED=true</code>.</li>
                <li>In the Meta dashboard, configure the webhook:
                  <div className="mt-1.5 bg-white/70 rounded p-2 font-mono text-[12px] flex items-center gap-2 flex-wrap">
                    <strong>URL:</strong><code>https://{webhookUrl}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://${webhookUrl}`);
                        setCopiedUrl(true);
                        setTimeout(() => setCopiedUrl(false), 1500);
                      }}
                      className="p-1 hover:bg-amber-100 rounded"
                      title="Copy"
                    >
                      {copiedUrl ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                    </button>
                  </div>
                  <div className="mt-1 bg-white/70 rounded p-2 font-mono text-[12px]">
                    <strong>Verify Token:</strong> <code>{verifyToken}</code>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Chat */}
        <div className="lg:col-span-2">
          <Card className="flex flex-col overflow-hidden" style={{ height: '640px' }}>
            {/* Chat header (WhatsApp-style) */}
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-3 text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center font-bold">
                K
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm">Kolly · RentKollect</p>
                <p className="text-[11px] text-emerald-100">AI assistant · typically replies in 2s</p>
              </div>
              <Button
                data-testid="kolly-reset-btn"
                onClick={resetSession}
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/15"
                title="Clear Kolly's memory"
              >
                <RotateCcw size={14} />
              </Button>
            </div>

            {/* Messages (WhatsApp-style bubbles) */}
            <div
              className="flex-1 overflow-y-auto p-4 space-y-2"
              style={{
                background: '#efeae2',
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22 viewBox=%220 0 200 200%22%3E%3Cpath fill=%22%23d9d2c8%22 fill-opacity=%220.4%22 d=%22M20 20h20v20H20zM60 60h20v20H60zM100 100h20v20h-20z%22/%3E%3C/svg%3E")'
              }}
              data-testid="kolly-chat-window"
            >
              {messages.length === 0 && (
                <div className="h-full flex items-center justify-center">
                  <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-3 max-w-sm text-center text-xs text-gray-700">
                    👋 Say "Hi Kolly" to start. Or tap a suggestion below.
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={m.id || `${m.role}-${i}`} msg={m} />
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-xl rounded-bl-sm px-3 py-2 shadow-sm flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }}></span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Quick suggestions */}
            <div className="px-3 py-2 bg-white border-t border-gray-100 flex gap-2 flex-wrap">
              {SUGGESTIONS.slice(0, 5).map((s) => (
                <button
                  key={s}
                  data-testid={`kolly-suggestion-${s.slice(0, 10).replace(/\s/g, '-')}`}
                  onClick={() => sendMessage(s)}
                  disabled={sending}
                  className="text-[11px] bg-gray-100 hover:bg-emerald-100 hover:text-emerald-700 text-gray-700 px-2.5 py-1 rounded-full transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="p-3 bg-gray-100 border-t border-gray-200 flex items-center gap-2"
            >
              <input
                data-testid="kolly-input"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Message Kolly..."
                disabled={sending}
                className="flex-1 bg-white rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
              />
              <Button
                data-testid="kolly-send-btn"
                type="submit"
                disabled={sending || !inputText.trim()}
                className="rounded-full w-10 h-10 p-0 bg-emerald-600 hover:bg-emerald-700"
              >
                <Send size={16} />
              </Button>
            </form>
          </Card>
        </div>

        {/* Side panel */}
        <div className="space-y-4">
          {/* Phone picker */}
          <Card className="p-4">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Test as phone</label>
            <input
              data-testid="kolly-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="mt-1.5 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 font-mono"
            />
            <p className="text-[11px] text-gray-500 mt-1.5">
              Must match a registered user in the DB. Your super-admin number (1234567890) works by default.
            </p>
          </Card>

          {/* Session state */}
          <Card className="p-4">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Zap size={12} /> Kolly's memory
            </h4>
            {session?.state && Object.keys(session.state).length > 0 ? (
              <div className="space-y-1.5">
                {Object.entries(session.state).map(([k, v]) => (
                  <div key={k} className="text-[12px] font-mono bg-gray-50 rounded px-2 py-1">
                    <span className="text-gray-500">{k}:</span>{' '}
                    <span className="text-gray-900">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic">No active context — fresh slate.</p>
            )}
          </Card>

          {/* Status */}
          {status && (
            <Card className="p-4">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">System</h4>
              <div className="space-y-1 text-[12px]">
                <StatusRow ok={status.has_phone_number_id} label="Phone Number ID" />
                <StatusRow ok={status.has_api_token} label="API Token" />
                <StatusRow ok={status.has_app_secret} label="App Secret" />
                <StatusRow ok={status.verify_token_set} label="Verify Token" />
                <StatusRow ok={status.whatsapp_enabled} label="WhatsApp enabled" critical />
                <div className="text-gray-400 text-[11px] mt-2 font-mono">model: {status.model}</div>
              </div>
            </Card>
          )}

          {/* Full suggestion list */}
          <Card className="p-4">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Try these</h4>
            <div className="space-y-1.5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => sendMessage(s)}
                  disabled={sending}
                  className="w-full text-left text-[12px] bg-gray-50 hover:bg-emerald-50 hover:text-emerald-700 text-gray-700 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const Bubble = ({ msg }) => {
  const isUser = msg.from === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[75%] px-3 py-2 shadow-sm text-sm whitespace-pre-wrap ${
          isUser
            ? 'bg-[#d9fdd3] rounded-xl rounded-br-sm'
            : 'bg-white rounded-xl rounded-bl-sm'
        } ${msg.pending ? 'opacity-60' : ''}`}
      >
        <MessageBody text={msg.text} />
        {msg.intent && !isUser && (
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap pt-1.5 border-t border-gray-100">
            <span className="text-[9px] font-bold tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
              {msg.intent}
            </span>
            {msg.slots && Object.keys(msg.slots).length > 0 && (
              <span className="text-[9px] text-gray-500 font-mono truncate">
                {JSON.stringify(msg.slots)}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Lightweight WhatsApp-style bold rendering: *bold*
// NOTE: index keys are intentional here — `parts` is regenerated whole from
// `text` on every render with no insert/reorder, which is the textbook
// correct case for index keys (no state-bleed risk).
const MessageBody = ({ text }) => {
  const parts = String(text || '').split(/(\*[^*]+\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('*') && p.endsWith('*') && p.length > 2 ? (
          // eslint-disable-next-line react/no-array-index-key
          <strong key={i}>{p.slice(1, -1)}</strong>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
};

const StatusRow = ({ ok, label, critical }) => (
  <div className="flex items-center justify-between">
    <span className="text-gray-600">{label}</span>
    <span
      className={`w-2 h-2 rounded-full ${
        ok ? 'bg-green-500' : critical ? 'bg-red-500' : 'bg-gray-300'
      }`}
    ></span>
  </div>
);

export default KollyTestChat;
